import React, { useState, useEffect } from 'react';
import { Shield, Zap, Coins, Settings, Users, Award, MessageCircle, Eye, Activity, DollarSign, History, Send, Crown, Star, Key, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

// Import tab components
import RolesTab from './AdminPanel/Roles';
import PointsTab from './AdminPanel/Points';
import SendPointsTab from './AdminPanel/SendPoints';
import RatesTab from './AdminPanel/Rates';
import UsersTab from './AdminPanel/Users';
import InboxTab from './AdminPanel/Inbox';
import PendingTab from './AdminPanel/Pending';
import ActiveTab from './AdminPanel/Active';
import WithdrawalsTab from './AdminPanel/Withdrawals';
import TransactionsTab from './AdminPanel/Transactions';
import PasswordManagementTab from './AdminPanel/PasswordManagement';
import VendorCouponsTab from './AdminPanel/VendorCoupons';

// Import shared modals
import CancelTournamentModal from './AdminPanel/CancelTournamentModal';
import ConfirmationModal from './AdminPanel/ConfirmationModal';

// UPDATED: Import the new multi-winner completion modal
import MultiWinnerCompletionModal from './AdminPanel/MultiWinnerCompletionModal';

import { useTranslation } from '../../utils/translationHelpers';
import './AdminPanel.css';

const AdminPanel = ({ user, tournaments, onTournamentUpdate, onUserUpdate, socket = null, language }) => {
  // Auto-detect language if not provided as prop
  const currentLanguage = language || localStorage.getItem('preferred-language') || 'en';
  
  // Translation hook using the detected language
  const { t } = useTranslation(currentLanguage);
  
  // Default tab based on role - sendpoints for admin/supervisor, pending for approver
  const getDefaultTab = () => {
    if (['main_admin', 'manager', 'supervisor'].includes(user?.role)) return 'sendpoints';
    return 'pending';
  };
  const [currentTab, setCurrentTab] = useState(getDefaultTab());
  const [stats, setStats] = useState({});
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [commissionRates, setCommissionRates] = useState({ 
    multiplayer: { approver: 25, system: 25 }, 
    battleRoyale: { approver: 12.5, system: 12.5 } 
  });
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [myApprovers, setMyApprovers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inboxStats, setInboxStats] = useState({ total: 0, open: 0, unassigned: 0 });
  const [countdowns, setCountdowns] = useState({});
  
  // Cancel tournament state
  const [cancelModal, setCancelModal] = useState({
    show: false,
    tournament: null,
    reason: '',
    loading: false
  });

  // UPDATED: Multi-winner completion modal state
  const [completionModal, setCompletionModal] = useState({
    show: false,
    tournament: null,
    selectedWinners: [],
    loading: false
  });

  // Legacy form state for backward compatibility
  const [forms, setForms] = useState({
    userSearch: { searchTerm: '', searchResults: [], selectedUser: null, newRole: 'player' },
    approver: { searchTerm: '', searchResults: [], selectedUser: null, multiplayerCommission: 0, battleRoyaleCommission: 0, showForm: false },
    pointManagement: { selectedUserId: '', pointsToSend: '', newPointsAmount: '' },
    ban: { searchTerm: '', banReason: '', searchResults: [] }
  });
  
  const [confirmations, setConfirmations] = useState({
    roleChange: { show: false, data: null },
    ban: { show: false, user: null },
    unban: { show: false, user: null },
    removeApprover: { show: false, approver: null }
  });

  // Enhanced toast helper functions
  const showInfoToast = (message, duration = 5000) => {
    toast(message, {
      duration,
      icon: 'ℹ️',
      style: {
        background: '#3b82f6',
        color: 'white',
        borderRadius: '8px',
        fontWeight: '500',
      },
    });
  };

  const showSuccessToast = (message, duration = 4000) => {
    toast.success(message, {
      duration,
      style: {
        background: '#10b981',
        color: 'white',
        borderRadius: '8px',
        fontWeight: '500',
      },
    });
  };

  const showErrorToast = (message, duration = 6000) => {
    toast.error(message, {
      duration,
      style: {
        background: '#ef4444',
        color: 'white',
        borderRadius: '8px',
        fontWeight: '500',
      },
    });
  };

  const showWarningToast = (message, duration = 5000) => {
    toast(message, {
      duration,
      icon: '⚠️',
      style: {
        background: '#f59e0b',
        color: 'white',
        borderRadius: '8px',
        fontWeight: '500',
      },
    });
  };

  // UPDATED: Get maximum allowed winners for ALL tournament types
  const getMaxAllowedWinners = (tournament, adminOverride = null) => {
    // If admin provides override, use it with safety limits
    if (adminOverride && adminOverride > 0) {
      const actualParticipants = (tournament.participants?.length || 0) + (tournament.fillers?.length || 0);
      return Math.min(adminOverride, actualParticipants, 10); // Cap at 10
    }

    // Battle Royale specific logic
    if (tournament.gameMode === 'battleRoyale') {
      switch (tournament.multiplayerType) {
        case 'duo':
          return 2;
        case 'squad':
          return 4;
        case 'solo':
          return 1;
        default:
          return 1;
      }
    }
    
    // NEW: All other game modes support flexible winners
    const actualParticipants = (tournament.participants?.length || 0) + (tournament.fillers?.length || 0);
    
    if (actualParticipants <= 2) {
      return 1;
    }
    
    // Allow up to half the participants, with minimum 1 and maximum 5
    return Math.max(1, Math.min(5, Math.floor(actualParticipants / 2)));
  };

  // UPDATED: Check if tournament supports multiple winners (now ALL can)
  const supportsMultipleWinners = (tournament) => {
    const maxWinners = getMaxAllowedWinners(tournament);
    return maxWinners > 1;
  };

  // UPDATED: Get winner selection display text for all game modes
  const getWinnerSelectionText = (tournament) => {
    const maxWinners = getMaxAllowedWinners(tournament);
    
    if (maxWinners === 1) {
      return 'Select Winner';
    }
    
    return `Select Winners (1-${maxWinners})`;
  };

  // UPDATED: Handle tournament completion with multi-winner support
  const handleCompleteTournament = (tournament) => {
    const maxWinners = getMaxAllowedWinners(tournament);
    
    if (maxWinners === 1) {
      // Single winner - use existing logic
      setCompletionModal({
        show: true,
        tournament,
        selectedWinners: [],
        loading: false
      });
    } else {
      // Multiple winners possible - use new multi-winner modal
      setCompletionModal({
        show: true,
        tournament,
        selectedWinners: [],
        loading: false
      });
    }
  };

  // UPDATED: Complete tournament with selected winners using Math.floor
  const completeTournamentWithWinners = async (tournament, selectedWinners) => {
    if (!selectedWinners || selectedWinners.length === 0) {
      showErrorToast('Please select at least one winner');
      return;
    }

    const maxWinners = getMaxAllowedWinners(tournament);
    if (selectedWinners.length > maxWinners) {
      showErrorToast(`Too many winners selected. Maximum allowed: ${maxWinners}`);
      return;
    }

    setCompletionModal(prev => ({ ...prev, loading: true }));

    try {
      console.log('🏆 Completing tournament (ALL game modes support):', {
        tournamentId: tournament._id,
        gameMode: tournament.gameMode,
        multiplayerType: tournament.multiplayerType,
        selectedWinners: selectedWinners,
        maxAllowedWinners: maxWinners
      });

      const response = await axios.post(`/api/tournaments/${tournament._id}/complete-with-fillers`, {
        winnerIds: selectedWinners,
        maxWinners: maxWinners // Send max winners to backend
      });

      console.log('✅ Tournament completion response:', response.data);

      const winnersCount = response.data.results?.winnersCount || selectedWinners.length;
      const prizePerWinner = response.data.results?.basePrizePerWinner || 0;
      
      let successMessage = 'Tournament completed successfully!';
      
      if (winnersCount > 1) {
        const modeDisplay = tournament.gameMode === 'battleRoyale' 
          ? `Battle Royale ${tournament.multiplayerType}`
          : tournament.gameMode;
        successMessage = `${modeDisplay} tournament completed! ${winnersCount} winners each received ${prizePerWinner} points (Math.floor applied).`;
      } else {
        successMessage = `Tournament completed! Winner received ${prizePerWinner} points.`;
      }

      showSuccessToast(successMessage, 6000);

      // Show commission info
      if (response.data.commission && response.data.commission.distributed) {
        const totalCommission = response.data.commission.totalCommission || 0;
        showInfoToast(`Commission distributed: ${totalCommission} points (Math.floor calculation)`, 5000);
      }

      // Show Math.floor info for transparency
      if (response.data.results?.flooredDistribution) {
        showInfoToast('Prize calculations used Math.floor (e.g., 145.5 → 145)', 4000);
      }

      setCompletionModal({
        show: false,
        tournament: null,
        selectedWinners: [],
        loading: false
      });

      onTournamentUpdate();
      onUserUpdate();

    } catch (error) {
      console.error('❌ Tournament completion error:', error);
      
      let errorMessage = 'Failed to complete tournament';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      showErrorToast(errorMessage);
      setCompletionModal(prev => ({ ...prev, loading: false }));
    }
  };

  // Close completion modal
  const closeCompletionModal = () => {
    setCompletionModal({
      show: false,
      tournament: null,
      selectedWinners: [],
      loading: false
    });
  };

  // Load inbox stats function
  const loadInboxStats = async () => {
    try {
      const response = await axios.get('/api/inbox/admin/cases?status=open');
      const cases = response.data.cases;
      
      setInboxStats({
        total: response.data.summary.total,
        open: response.data.summary.open,
        unassigned: cases.filter(c => !c.assignedTo).length
      });
    } catch (error) {
      console.error('Load inbox stats error:', error);
    }
  };

  // Real-time updates for role changes and inbox
  useEffect(() => {
    if (socket && socket.connected) {
      const handleUserRoleChanged = (data) => {
        showInfoToast(t('roleChangedSuccessfully', currentLanguage, { username: data.inGameName, role: data.newRole }), 3000);
        loadData();
      };

      const handleTournamentCancelled = (data) => {
        showInfoToast(t('tournamentCancelledSuccessfully', currentLanguage, { refundInfo: data.reason }), 4000);
        onTournamentUpdate();
        onUserUpdate();
      };

      const handleNewInboxReport = () => {
        if (['main_admin', 'supervisor'].includes(user.role)) {
          loadInboxStats();
        }
      };

      const handleInboxMessageReceived = () => {
        if (['main_admin', 'supervisor'].includes(user.role)) {
          loadInboxStats();
        }
      };

      socket.on('userRoleChanged', handleUserRoleChanged);
      socket.on('tournamentCancelled', handleTournamentCancelled);
      socket.on('newInboxReport', handleNewInboxReport);
      socket.on('inboxMessageReceived', handleInboxMessageReceived);

      return () => {
        socket.off('userRoleChanged', handleUserRoleChanged);
        socket.off('tournamentCancelled', handleTournamentCancelled);
        socket.off('newInboxReport', handleNewInboxReport);
        socket.off('inboxMessageReceived', handleInboxMessageReceived);
      };
    }
  }, [socket, currentLanguage, user.role, t]);

  useEffect(() => { 
    loadData(); 
  }, [user.role]);

  const loadData = async () => {
    try {
      const [statsRes, withdrawalsRes, ratesRes] = await Promise.all([
        axios.get('/api/admin/stats'),
        user.role === 'main_admin' ? axios.get('/api/payments/withdrawals') : Promise.resolve({ data: [] }),
        axios.get('/api/admin/commission-rates')
      ]);

      setStats(statsRes.data);
      setWithdrawalRequests(withdrawalsRes.data);
      setCommissionRates(ratesRes.data || { multiplayer: { approver: 25, system: 25 }, battleRoyale: { approver: 12.5, system: 12.5 } });

      if (['main_admin', 'manager'].includes(user.role)) {
        const usersRes = await axios.get('/api/admin/users?page=1&limit=20');
        setUsers(usersRes.data.users);
        if (usersRes.data.pagination) setUsersPagination(usersRes.data.pagination);
      }
      if (user.role === 'supervisor') {
        const approversRes = await axios.get('/api/admin/my-approvers');
        setMyApprovers(approversRes.data);
      }

      if (['main_admin', 'manager', 'supervisor'].includes(user.role)) {
        loadInboxStats();
      }
    } catch (error) {
      showErrorToast(t('failedToLoadAdminData', currentLanguage));
      setCommissionRates({ multiplayer: { approver: 25, system: 25 }, battleRoyale: { approver: 12.5, system: 12.5 } });
    }
  };

  // Cancel tournament handler
  const handleCancelTournament = async (tournament, reason) => {
    setCancelModal(prev => ({ ...prev, loading: true }));
    
    try {
      const response = await axios.post(`/api/tournaments/${tournament._id}/cancel`, {
        reason: reason || t('cancellationReason', currentLanguage)
      });

      showSuccessToast(t('tournamentCancelledSuccessfully', currentLanguage, { refundInfo: response.data.refundInfo || '' }));
      setCancelModal({ show: false, tournament: null, reason: '', loading: false });
      
      onTournamentUpdate();
      onUserUpdate();

    } catch (error) {
      const message = error.response?.data?.message || t('failedToCancelTournament', currentLanguage);
      showErrorToast(message);
      setCancelModal(prev => ({ ...prev, loading: false }));
    }
  };

  const openCancelModal = (tournament) => {
    setCancelModal({
      show: true,
      tournament,
      reason: '',
      loading: false
    });
  };

  const closeCancelModal = () => {
    setCancelModal({
      show: false,
      tournament: null,
      reason: '',
      loading: false
    });
  };

  // Check if user can cancel tournament
  const canCancelTournament = (tournament) => {
    if (['main_admin', 'manager'].includes(user.role)) return true;

    if (['supervisor', 'approver'].includes(user.role)) {
      // Can cancel if they approved it
      if (tournament.approvedBy && tournament.approvedBy._id === user._id) {
        return true;
      }
      // Supervisors can also cancel giveaway matches they created
      if (user.role === 'supervisor' &&
          tournament.tournamentType === 'giveaway' &&
          tournament.creator &&
          (tournament.creator._id || tournament.creator) === user._id) {
        return true;
      }
    }

    return false;
  };

  // Get role display information
  const getRoleInfo = (role) => {
    const roleMap = {
      'main_admin': { 
        display: t('mainAdmin', currentLanguage), 
        color: 'red', 
        className: 'main-admin',
        icon: <Crown className="w-4 h-4" />,
        description: t('fullSystemAccess', currentLanguage)
      },
      'manager': { 
        display: t('manager', currentLanguage) || 'Manager', 
        color: 'orange', 
        className: 'manager',
        icon: <Star className="w-4 h-4" />,
        description: t('managerAccess', currentLanguage) || 'Supervisor access + ban users and change roles'
      },
      'supervisor': { 
        display: t('supervisor', currentLanguage), 
        color: 'purple', 
        className: 'supervisor',
        icon: <Star className="w-4 h-4" />,
        description: t('canManageApprovers', currentLanguage)
      },
      'approver': { 
        display: t('approver', currentLanguage), 
        color: 'blue', 
        className: 'approver',
        icon: <Award className="w-4 h-4" />,
        description: t('canApproveTournaments', currentLanguage)
      },
      'verified_sponsor': { 
        display: t('verifiedSponsor', currentLanguage) || 'Verified Sponsor', 
        color: 'gold', 
        className: 'verified-sponsor',
        icon: <Award className="w-4 h-4" />,
        description: t('verifiedSponsorAccess', currentLanguage) || 'Can create giveaway and bracket tournaments'
      },
      'speedos': { 
        display: t('speedos', currentLanguage) || 'Speedos', 
        color: 'cyan', 
        className: 'speedos',
        icon: <Package className="w-4 h-4" />,
        description: 'Speedos vendor - manages Speedos package coupons'
      },
      'ryder': { 
        display: t('ryder', currentLanguage) || 'Ryder', 
        color: 'teal', 
        className: 'ryder',
        icon: <Package className="w-4 h-4" />,
        description: 'Ryder vendor - manages Ryder package coupons'
      },
      'player': { 
        display: t('player', currentLanguage), 
        color: 'green', 
        className: 'player',
        icon: <Users className="w-4 h-4" />,
        description: t('regularUser', currentLanguage)
      }
    };
    return roleMap[role] || roleMap['player'];
  };

  
  const fetchUsersPage = async (page) => {
    try {
      const usersRes = await axios.get('/api/admin/users?page=' + page + '&limit=20');
      setUsers(usersRes.data.users);
      if (usersRes.data.pagination) setUsersPagination(usersRes.data.pagination);
    } catch (error) {
      console.error('Failed to fetch users page:', error);
    }
  };

  const searchUsers = async (searchTerm, formType = 'user') => {
    if (!searchTerm.trim()) {
      setForms(prev => ({ ...prev, [formType]: { ...prev[formType], searchResults: [] } }));
      return;
    }
    try {
      const response = await axios.get(`/api/users/search?q=${searchTerm}`);
      setForms(prev => ({ ...prev, [formType]: { ...prev[formType], searchResults: response.data } }));
    } catch (error) {
      console.error('Search users error:', error);
    }
  };

  const handleAction = async (action, data) => {
    setLoading(true);
    try {
      let response;
      switch (action) {
        case 'changeRole':
          response = await axios.post(`/api/admin/users/${data.userId}/role`, { role: data.newRole });
          showSuccessToast(t('roleChangedSuccessfully', currentLanguage, { username: data.username, role: data.newRole }));
          break;
        case 'ban':
          response = await axios.post(`/api/admin/users/${data._id}/ban`, { banned: true, banReason: forms.ban.banReason });
          showSuccessToast(t('userBannedSuccessfully', currentLanguage, { username: data.inGameName }));
          break;
        case 'unban':
          response = await axios.post(`/api/admin/users/${data._id}/ban`, { banned: false });
          showSuccessToast(t('userUnbannedSuccessfully', currentLanguage, { username: data.inGameName }));
          break;
        case 'addApprover':
          response = await axios.post('/api/admin/approvers', {
            userId: data.selectedUser._id,
            multiplayerCommission: data.multiplayerCommission,
            battleRoyaleCommission: data.battleRoyaleCommission
          });
          showSuccessToast(t('approverAddedSuccessfully', currentLanguage));
          break;
        case 'removeApprover':
          response = await axios.post(`/api/admin/approvers/${data._id}/remove`);
          showSuccessToast(t('approverRemovedSuccessfully', currentLanguage, { username: data.inGameName }));
          break;
        default:
          console.error('Unknown action:', action);
          showErrorToast(t('unknownAction', currentLanguage, { action }));
          return;
      }
      resetForms();
      resetConfirmations();
      loadData();
    } catch (error) {
      console.error(`Action ${action} failed:`, error);
      showErrorToast(error.response?.data?.message || `Failed to ${action}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForms = () => {
    setForms({
      userSearch: { searchTerm: '', searchResults: [], selectedUser: null, newRole: 'player' },
      approver: { searchTerm: '', searchResults: [], selectedUser: null, multiplayerCommission: 0, battleRoyaleCommission: 0, showForm: false },
      pointManagement: { selectedUserId: '', pointsToSend: '', newPointsAmount: '' },
      ban: { searchTerm: '', banReason: '', searchResults: [] }
    });
  };

  const resetConfirmations = () => {
    setConfirmations({
      roleChange: { show: false, data: null },
      ban: { show: false, user: null },
      unban: { show: false, user: null },
      removeApprover: { show: false, approver: null }
    });
  };

  // Enhanced tournament filtering based on user role and ownership
  const getFilteredTournaments = (tournaments, status) => {
    const filtered = tournaments.filter(t => {
      if (Array.isArray(status)) {
        return status.includes(t.status);
      }
      return t.status === status;
    });

    if (user.role === 'main_admin') {
      return filtered;
    }

    return filtered.filter(t => {
      if (t.status === 'ready_for_approval') {
        return !t.approvedBy || t.approvedBy._id === user._id || t.approvedBy === user._id;
      }
      
      if (['countdown', 'active'].includes(t.status)) {
        return t.approvedBy && (t.approvedBy._id === user._id || t.approvedBy === user._id);
      }
      
      return true;
    });
  };

  // Enhanced tabs with shorter, mobile-friendly labels
  const tabs = [
    // Send Points tab - available for main_admin and supervisor
    ...(['main_admin', 'manager', 'supervisor'].includes(user.role) ? [
      { id: 'sendpoints', label: t('sendPoints', currentLanguage) || 'Send Points', icon: <Send className="w-4 h-4" />, count: null }
    ] : []),
    // Role management and Users - available for main_admin and manager
    ...(['main_admin', 'manager'].includes(user.role) ? [
      { id: 'rolemanagement', label: t('roles', currentLanguage), icon: <Zap className="w-4 h-4" />, count: null },
      { id: 'users', label: t('users', currentLanguage), icon: <Users className="w-4 h-4" />, count: users.filter(u => u.banned).length }
    ] : []),
    // Points and Commission - main_admin only
    ...(user.role === 'main_admin' ? [
      { id: 'points', label: t('pointsTab', currentLanguage), icon: <Coins className="w-4 h-4" />, count: null },
      { id: 'commission', label: t('rates', currentLanguage), icon: <Settings className="w-4 h-4" />, count: null }
    ] : []),
    ...(user.role === 'supervisor' ? [
      { id: 'approvers', label: t('approvers', currentLanguage), icon: <Award className="w-4 h-4" />, count: myApprovers.length }
    ] : []),
    ...(['main_admin', 'manager', 'supervisor'].includes(user.role) ? [
      { id: 'inbox', label: t('inbox', currentLanguage), icon: <MessageCircle className="w-4 h-4" />, count: inboxStats.unassigned }
    ] : []),
    { id: 'pending', label: t('pending', currentLanguage), icon: <Eye className="w-4 h-4" />, count: getFilteredTournaments(tournaments, 'ready_for_approval').length },
    { id: 'ongoing', label: t('active', currentLanguage), icon: <Activity className="w-4 h-4" />, count: getFilteredTournaments(tournaments, ['countdown', 'active']).length },
    ...(user.role === 'main_admin' ? [
      { id: 'requests', label: t('withdrawals', currentLanguage), icon: <DollarSign className="w-4 h-4" />, count: withdrawalRequests.filter(w => w.status === 'pending').length },
      { id: 'transactions', label: t('transactionLog', currentLanguage), icon: <History className="w-4 h-4" />, count: null },
      { id: 'password', label: 'Password', icon: <Key className="w-4 h-4" />, count: null },
      { id: 'vendorcoupons', label: 'Vendor Coupons', icon: <Package className="w-4 h-4" />, count: null }
    ] : []),
    ...(user.role === 'manager' ? [
      { id: 'password', label: 'Password', icon: <Key className="w-4 h-4" />, count: null }
    ] : [])
  ];

  // Shared props object - UPDATED with multi-winner completion handler
  const sharedProps = {
    user,
    loading,
    setLoading,
    language: currentLanguage,
    t,
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showInfoToast,
    loadData,
    onTournamentUpdate,
    onUserUpdate,
    // UPDATED: Multi-winner support functions
    getMaxAllowedWinners,
    supportsMultipleWinners,
    getWinnerSelectionText,
    handleCompleteTournament,
    completeTournamentWithWinners
  };

  return (
    <div className="admin-panel">
      <h3 className="admin-panel-title">
        <Shield className="admin-panel-icon" />
        {user.role === 'main_admin' ? t('mainAdminPanel') : 
         user.role === 'manager' ? t('managerPanel') :
         user.role === 'supervisor' ? t('supervisorPanel') : 
         t('approverPanel')}
      </h3>

      {user.role !== 'main_admin' && (
        <div className="commission-summary">
          <h4 className="commission-summary-title">{t('commissionSummary', currentLanguage)}</h4>
          <div className="commission-single">
            <p className="commission-value">{stats.commission?.userCommission || 0}</p>
            <p className="commission-label">
              {t('yourCommissionEarned', currentLanguage)} {user.role === 'supervisor' && t('asSupervisor', currentLanguage)}{user.role === 'approver' && t('asApprover', currentLanguage)}
            </p>
          </div>
        </div>
      )}

      <div className="admin-tabs">
        {tabs.map(tab => (
          <button 
            key={tab.id} 
            onClick={() => setCurrentTab(tab.id)}
            className={`admin-tab ${currentTab === tab.id ? 'active' : 'inactive'}`}
          >
            <span className="tab-content">
              {tab.icon} 
              <span className="tab-label">{tab.label}</span>
              {tab.count !== null && tab.count > 0 && (
                <span className="tab-badge">{tab.count}</span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {currentTab === 'sendpoints' && ['main_admin', 'manager', 'supervisor'].includes(user.role) && (
        <SendPointsTab {...sharedProps} />
      )}

      {currentTab === 'rolemanagement' && ['main_admin', 'manager'].includes(user.role) && (
        <RolesTab {...sharedProps} getRoleInfo={getRoleInfo} />
      )}

      {currentTab === 'points' && user.role === 'main_admin' && (
        <PointsTab {...sharedProps} />
      )}
      
      {currentTab === 'commission' && user.role === 'main_admin' && 
        <RatesTab 
          {...sharedProps}
          commissionRates={commissionRates} 
          setCommissionRates={setCommissionRates} 
        />
      }

      {currentTab === 'users' && ['main_admin', 'manager'].includes(user.role) && 
        <UsersTab 
          {...sharedProps}
          users={users} 
          forms={forms} 
          setForms={setForms} 
          searchUsers={searchUsers} 
          setConfirmations={setConfirmations}
          pagination={usersPagination}
          onPageChange={fetchUsersPage}
        />
      }

      {currentTab === 'approvers' && user.role === 'supervisor' && 
        <UsersTab 
          {...sharedProps}
          myApprovers={myApprovers} 
          commissionRates={commissionRates} 
          forms={forms} 
          setForms={setForms} 
          searchUsers={searchUsers} 
          handleAction={handleAction} 
          setConfirmations={setConfirmations} 
          isApproverTab={true}
        />
      }

      {currentTab === 'inbox' && ['main_admin', 'manager', 'supervisor'].includes(user.role) &&
        <InboxTab {...sharedProps} socket={socket} />
      }

      {currentTab === 'pending' && 
        <PendingTab 
          {...sharedProps}
          tournaments={getFilteredTournaments(tournaments, 'ready_for_approval')} 
          onCancel={openCancelModal}
        />
      }

      {currentTab === 'ongoing' && 
        <ActiveTab 
          {...sharedProps}
          tournaments={getFilteredTournaments(tournaments, ['countdown', 'active'])} 
          onCancel={canCancelTournament ? openCancelModal : null}
          countdowns={countdowns}
          commissionRates={commissionRates}
        />
      }

      {currentTab === 'requests' && user.role === 'main_admin' &&
        <WithdrawalsTab
          {...sharedProps}
          requests={withdrawalRequests.filter(w => w.status === 'pending')}
        />
      }

      {currentTab === 'transactions' && user.role === 'main_admin' &&
        <TransactionsTab {...sharedProps} />
      }

      {currentTab === 'vendorcoupons' && user.role === 'main_admin' &&
        <VendorCouponsTab {...sharedProps} />
      }
      {currentTab === 'password' && (user.role === 'main_admin' || user.role === 'manager') &&
        <PasswordManagementTab {...sharedProps} />
      }


      {/* Cancel Tournament Modal */}
      {cancelModal.show && (
        <CancelTournamentModal
          tournament={cancelModal.tournament}
          reason={cancelModal.reason}
          setReason={(reason) => setCancelModal(prev => ({ ...prev, reason }))}
          onConfirm={() => handleCancelTournament(cancelModal.tournament, cancelModal.reason)}
          onCancel={closeCancelModal}
          loading={cancelModal.loading}
          language={currentLanguage}
          t={t}
        />
      )}

      {/* UPDATED: Multi-Winner Tournament Completion Modal */}
      {completionModal.show && (
        <MultiWinnerCompletionModal
          tournament={completionModal.tournament}
          selectedWinners={completionModal.selectedWinners}
          onWinnerSelectionChange={(winners) => setCompletionModal(prev => ({ ...prev, selectedWinners: winners }))}
          onConfirm={() => completeTournamentWithWinners(completionModal.tournament, completionModal.selectedWinners)}
          onCancel={closeCompletionModal}
          loading={completionModal.loading}
          language={currentLanguage}
          t={t}
          getMaxAllowedWinners={getMaxAllowedWinners}
          supportsMultipleWinners={supportsMultipleWinners}
        />
      )}

      {/* Confirmation Modals */}
      {Object.entries(confirmations).map(([key, conf]) => 
        conf.show && (
          <ConfirmationModal 
            key={key} 
            type={key} 
            data={conf} 
            onConfirm={() => handleAction(key, conf.data || conf.user || conf.approver)} 
            onCancel={resetConfirmations} 
            loading={loading} 
            language={currentLanguage}
            t={t}
          />
        )
      )}
    </div>
  );
};

export default AdminPanel;